/**
 * V2BackdropSection — V2-native editor section for step.content.canvas.background
 *
 * Edits: Background type (solid/photo/clip), color, image, video
 * V2 paths: content.canvas.background.{kind, content.*}
 *
 * Replaces: OnboardingBackgroundSection (for V2 context)
 * Reuses: StyleFieldRow, ColorInput, ImageUploadElement, VideoUploadElement
 *
 * V2 Backdrop is a discriminated union:
 *   { kind: 'solid', content: { color: string } }
 *   { kind: 'photo', content: { image: LocalizedImage } }
 *   { kind: 'clip',  content: { video: LocalizedClip } }
 *
 * Photo/clip assets use LocalizedImage/LocalizedClip with translations.{locale}: AssetReference
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import React, { useCallback, useState, useMemo } from 'react';
import { Box, FormControl, Select, MenuItem } from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { VideoUploadElement } from '../../../factories/elements/VideoUploadElement';
import { StyleFieldRow } from '../../shared/styleFields';
import { EditorGuideProvider } from '../../../contexts';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import {
  V2_BACKDROP_KIND_OPTIONS,
  V2_BACKDROP_COLOR_CONFIG,
  V2_BACKDROP_IMAGE_CONFIG,
  V2_BACKDROP_VIDEO_CONFIG,
  V2_WEB_VIEW_BG_COLOR_CONFIG,
  type V2BackdropKind,
} from '../../../config/v2BackdropConfig';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface V2BackdropSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** Project ID for upload service */
  projectId?: string;

  /** Onboarding ID for upload service */
  onboardingId?: string;

  /** Version ID for upload service */
  versionId?: string;

  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];
}

export const V2BackdropSection: React.FC<V2BackdropSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  availableLocales = [],
}) => {
  const [uploading, setUploading] = useState(false);

  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  // Get current backdrop kind (V2: solid/photo/clip)
  const backdropKind = useMemo<V2BackdropKind>(() => {
    const kind = getValueByPath<string>(step, 'content.canvas.background.kind');
    return (kind as V2BackdropKind) ?? 'solid';
  }, [step]);

  // Get color value — nested under content.color (SolidBackdrop)
  const colorValue = useMemo(() => {
    return getValueByPath<string>(step, V2_BACKDROP_COLOR_CONFIG.path) ?? '#ffffff';
  }, [step]);

  // Get image URL — LocalizedImage: content.image.translations.{locale}.original
  const imageUrl = useMemo(() => {
    return getValueByPath<string>(
      step,
      `content.canvas.background.content.image.translations.${locale}.original`
    ) ?? null;
  }, [step, locale]);

  // Get video URL — LocalizedClip: content.video.translations.{locale}.original
  const videoUrl = useMemo(() => {
    return getValueByPath<string>(
      step,
      `content.canvas.background.content.video.translations.${locale}.original`
    ) ?? null;
  }, [step, locale]);

  // Check if image/video are enabled (non-null at their path)
  const isImageEnabled = useMemo(() => {
    return isFieldEnabled(step, V2_BACKDROP_IMAGE_CONFIG.path);
  }, [step]);

  const isVideoEnabled = useMemo(() => {
    return isFieldEnabled(step, V2_BACKDROP_VIDEO_CONFIG.path);
  }, [step]);

  // Handle kind change — reset content to match new kind
  const handleKindChange = useCallback(
    (event: { target: { value: string } }) => {
      const newKind = event.target.value as V2BackdropKind;

      if (newKind === 'solid') {
        onChange('content.canvas.background', {
          kind: newKind,
          content: { color: colorValue || '#ffffff' },
        });
      } else if (newKind === 'photo') {
        onChange('content.canvas.background', {
          kind: newKind,
          content: {
            image: { translations: {}, appearance: {} },
          },
        });
      } else {
        onChange('content.canvas.background', {
          kind: newKind,
          content: {
            video: { translations: {}, appearance: {} },
          },
        });
      }
    },
    [onChange, colorValue]
  );

  // Handle color change — writes to content.canvas.background.content.color
  // Safety: also ensure 'kind' is set (handles legacy data without kind)
  const handleColorChange = useCallback(
    (value: unknown) => {
      const existingKind = getValueByPath<string>(step, 'content.canvas.background.kind');
      if (!existingKind) {
        onChange('content.canvas.background.kind', 'solid');
      }
      onChange(V2_BACKDROP_COLOR_CONFIG.path, value);
    },
    [onChange, step]
  );

  // Handle image URL change — writes to LocalizedImage.translations.{locale}
  const handleImageChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`content.canvas.background.content.image.translations.${locale}`, {
          original: value,
          small: value,
        });
      } else {
        onChange('content.canvas.background.content.image', null);
      }
    },
    [onChange, locale]
  );

  // Handle video URL change — writes to LocalizedClip.translations.{locale}
  const handleVideoChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`content.canvas.background.content.video.translations.${locale}`, {
          original: value,
        });
      } else {
        onChange('content.canvas.background.content.video', null);
      }
    },
    [onChange, locale]
  );

  // Handle image enabled toggle
  const handleImageEnabledChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        onChange('content.canvas.background.content', {
          image: { translations: {}, appearance: {} },
        });
      } else {
        onChange('content.canvas.background.content.image', null);
      }
    },
    [onChange]
  );

  // Handle video enabled toggle
  const handleVideoEnabledChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        onChange('content.canvas.background.content', {
          video: { translations: {}, appearance: {} },
        });
      } else {
        onChange('content.canvas.background.content.video', null);
      }
    },
    [onChange]
  );

  // Handle image upload — writes AssetReference to LocalizedImage.translations.{locale}
  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        });
        onChange(`content.canvas.background.content.image.translations.${locale}`, {
          original: result.assetUrl.origin,
          small: result.assetUrl.small,
        });
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  // Handle video upload — writes AssetReference to LocalizedClip.translations.{locale}
  const handleVideoUpload = useCallback(
    async (file: File, onProgress?: (fraction: number) => void): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadVideo(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        }, { onProgress });
        onChange(`content.canvas.background.content.video.translations.${locale}`, {
          original: result.assetUrl.origin,
        });
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  const SECTION_NAME = 'Background';

  return (
    <SAccordion
      title={SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Kind Selector — V2 uses solid/photo/clip */}
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
          <FormControl fullWidth size="small">
            <Select
              value={backdropKind}
              onChange={handleKindChange}
              disabled={disabled}
              data-testid="backdrop-kind-select"
            >
              {V2_BACKDROP_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Solid Color (when kind='solid') */}
        {backdropKind === 'solid' && (
          <Box data-testid="backdrop-solid-color">
            <StyleFieldRow
              config={V2_BACKDROP_COLOR_CONFIG}
              value={colorValue}
              onChange={handleColorChange}
              disabled={disabled}
            />
          </Box>
        )}

        {/* Photo (when kind='photo') */}
        {backdropKind === 'photo' && (
          <ImageUploadElement
            data-testid="field-backdrop-image"
            uploadButtonTestId="upload-btn-backdrop-image"
            fieldConfig={V2_BACKDROP_IMAGE_CONFIG}
            value={imageUrl}
            templateValue={null}
            enabled={isImageEnabled}
            locale={locale}
            onChange={handleImageChange}
            onEnabledChange={handleImageEnabledChange}
            onUpload={handleImageUpload}
            disabled={disabled || uploading}
            expandedStyles={false}
            onStylesExpandToggle={() => {}}
            styleValues={{}}
            onStyleChange={() => {}}
          />
        )}

        {/* Video Clip (when kind='clip') */}
        {backdropKind === 'clip' && (
          <VideoUploadElement
            data-testid="field-backdrop-video"
            uploadButtonTestId="upload-btn-backdrop-video"
            fieldConfig={V2_BACKDROP_VIDEO_CONFIG}
            value={videoUrl}
            templateValue={null}
            enabled={isVideoEnabled}
            locale={locale}
            onChange={handleVideoChange}
            onEnabledChange={handleVideoEnabledChange}
            onUpload={handleVideoUpload}
            disabled={disabled || uploading}
            expandedStyles={false}
            onStylesExpandToggle={() => {}}
            styleValues={{}}
            onStyleChange={() => {}}
          />
        )}

        {/* Browser chrome color (meta theme-color + body background) */}
        <Box data-testid="backdrop-chrome-color" sx={{ mt: FIELD_MARGIN_BOTTOM }}>
          <StyleFieldRow
            config={V2_WEB_VIEW_BG_COLOR_CONFIG}
            value={getValueByPath<string>(step, V2_WEB_VIEW_BG_COLOR_CONFIG.path) ?? null}
            onChange={(newValue) => onChange(V2_WEB_VIEW_BG_COLOR_CONFIG.path, newValue)}
            disabled={disabled}
          />
        </Box>
      </EditorGuideProvider>
    </SAccordion>
  );
};
